import * as bootstrap from 'bootstrap';

const jsKit = (() => {
  const defaults = {
    modalID: '#popup_container',
    debugMode: false,
  };

  const currentAbortController = null;

  const rwdMedia = {};
  const delegatedEvents = {};
  let delegatedEventOrder = 0;
  const getBootstrap = () => bootstrap;
  const getCtoken = () => document.cookie.split('; ')
    .find((cookie) => cookie.startsWith('ctoken='))
    ?.split('=').slice(1).join('') || '';
  const appendCtoken = (data) => {
    const token = getCtoken();
    if (!token || !data || (!(data instanceof FormData) && !(data instanceof URLSearchParams))) {
      return;
    }

    const formNames = new Set([...data.keys()]
      .map((key) => key.match(/^([^[]+)\[/)?.[1])
      .filter(Boolean));
    formNames.forEach((formName) => {
      const tokenKey = `${formName}[ctoken]`;
      if (!data.has(tokenKey)) {
        data.set(tokenKey, token);
      }
    });
  };

  ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].forEach((bp) => rwdMedia[bp] = () => document.querySelector(`.media-${bp}`).offsetParent !== null);

  return {
    currentAbortController,

    defaults,

    rwdMedia,

    isIOS: () => /iPhone|iPad|iPod/i.test(navigator.userAgent),

    isAndroid: () => /Android/i.test(navigator.userAgent),

    isMobile: () => jsKit.isIOS() || jsKit.isAndroid(),

    getIndex: (nodeList, element) => Array.from(nodeList).indexOf(element),

    requestUrl: async (url, dataType = 'json', method = 'GET', data = null, options = {}) => {
      const headers = {};
      let body;
      let signal;

      if (method === 'POST' && options.appendCtoken !== false) {
        appendCtoken(data);
      }

      if (options.abortPrevious) {
        if (jsKit.currentAbortController) {
          jsKit.currentAbortController.abort();
        }

        jsKit.currentAbortController = new AbortController();
        signal = jsKit.currentAbortController.signal;
      } else if (options.signal) {
        signal = options.signal;
      }

      if (!(data instanceof FormData)) {
        if (dataType === 'json') {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(data);
        } else if (dataType === 'text') {
          headers['Content-Type'] = 'text/plain';
          body = data;
        } else if (dataType === 'urlencoded' && data) {
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
          body = new URLSearchParams(data).toString();
        }
      } else {
        body = data;
      }

      try {
        const response = await fetch(url, {
          method,
          ...(method === 'POST' && data ? { body } : {}),
          headers,
          ...(signal ? { signal } : {}),
        });

        return dataType === 'json' || dataType === 'urlencoded' ? await response.json() : await response.text();
      } catch (error) {
        console.log('Wystąpił błąd: ', error);
      }
    },

    fetchUrl: async (url, cb, dataType = 'json', method = 'GET', data = null, options = {}) => {
      const result = await jsKit.requestUrl(url, dataType, method, data, options);
      if (typeof cb === 'function' && typeof result !== 'undefined') {
        cb(result);
      }
    },

    getDataParts: (element, dataItem, separator = ', ') => {
      if (!element || !element.dataset || !element.dataset[dataItem]) {
        return [];
      }

      return element.dataset[dataItem].split(separator);
    },

    setLoading: (element, isLoading = true, options = {}) => {
      if (!element) return;

      const { disabled = false, className = 'loading' } = options;
      element.classList.toggle(className, isLoading);

      if (disabled && 'disabled' in element) {
        element.disabled = isLoading;
      }
    },

    setHtml: (target, html) => {
      const element = typeof target === 'string' ? document.querySelector(target) : target;
      if (element) {
        element.innerHTML = html;
      }

      return element;
    },

    initTooltipsIn: (parentElem) => {
      if (!parentElem) return;
      jsKit.initTooltip(parentElem.querySelectorAll('[data-bs-toggle="tooltip"]'));
    },

    disposeTooltip: (element) => {
      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      const tooltip = bootstrap.Tooltip.getInstance(element);
      tooltip?.dispose();
    },

    validateForm: (form) => {
      const invalidFields = Array.from(form.querySelectorAll('[data-js-validate][data-js-invalid-text]'))
        .map((input) => jsKit.validateInput(input))
        .filter((isValid) => isValid === false);

      return invalidFields.length === 0;
    },

    handleValidatedResponse: async (data, options = {}) => {
      if (!data) return;

      const {
        onValid = null,
        onInvalid = null,
        errorType = 'error',
        showError = true,
      } = options;

      if (data.validated) {
        if (typeof onValid === 'function') {
          await onValid(data);
        }
        return;
      }

      if (typeof onInvalid === 'function') {
        await onInvalid(data);
        return;
      }

      if (showError && data.error_message) {
        jsKit.showNotificationsPanel(data.error_message, errorType);
      }
    },

    getAppId: () => document.head.dataset.appId,

    getLang: () => {
      const lang = document.documentElement?.getAttribute('lang') || 'pl';
      return lang.toLowerCase().slice(0, 2);
    },

    isNavigatorPl: () => navigator.language.startsWith('pl'),

    closeModal: () => {
      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);
      if (isOpen) {
        bsModal.hide();
      }
    },

    emptyModal: () => {
      const domLargeModal = document.querySelector(defaults.modalID);
      domLargeModal.querySelector('.modal-content').innerHTML = '';
    },

    showModal: (modalID) => {
      if (!document.querySelector(modalID)) {
        return;
      }

      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      const modal = bootstrap.Modal.getOrCreateInstance(document.querySelector(modalID));
      modal.show();
    },

    openModal: (html, customs = {}) => {
      if (defaults.debugMode) {
        console.log('openModal()', customs);
      }

      const defaultOptions = {
        size: 'modal-lg',
        config: { backdrop: 'true' },
        shownCB: false,
        hiddenCB: false,
      };
      const options = { ...defaultOptions, ...customs};
      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);

      if (isOpen) {
        bsModal.hide();
      }

      domLargeModal.addEventListener('shown.bs.modal', () => {
        jsKit.autofocus(domLargeModal);
        if (options.shownCB) {
          options.shownCB();
        }
      }, { once: true });

      domLargeModal.addEventListener('hidden.bs.modal', () => {
        jsKit.emptyModal();
        if (options.hiddenCB) {
          options.hiddenCB();
        }
      }, { once: true });

      jsKit.emptyModal();
      domLargeModal.innerHTML = html;
      bsModal.show();
    },

    openModalJson: async (url, options = {}) => {
      const data = await jsKit.requestUrl(url);

      if (!data?.validated) {
        jsKit.showNotificationsPanel(data?.message || data?.error_message || 'Wystąpił błąd', 'error');
        return;
      }

      const content = await jsKit.requestUrl(url, 'urlencoded', 'POST', new URLSearchParams());
      const bootstrap = getBootstrap();
      const popup = document.getElementById('popup_container');
      const popupContent = popup?.querySelector('.modal-content');

      if (!bootstrap || !popup || !popupContent || !content?.content) {
        return;
      }

      popupContent.innerHTML = content.content;
      bootstrap.Modal.getOrCreateInstance(popup, { ...options, show: true }).show();
      popup.addEventListener('hidden.bs.modal', () => {
        popupContent.innerHTML = '';
      }, { once: true });
    },

    modalData: async (e) => {
      if (defaults.debugMode) {
        console.log('modalData()');
      }

      const { target } = e;
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.add('loading');
      }

      const url = target.dataset.jsModalData;
      if (!url) return;

      const getCB = (html) => {
        jsKit.openModal(html);
        if (isBtn) {
          target.classList.remove('loading');
        }
      };
      await jsKit.fetchUrl(url, (html) => getCB(html));
    },

    containerData: async (e) => {
      if (defaults.debugMode) {
        console.log('containerData()');
      }

      const { target } = e;
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.add('loading');
      }

      const [url, containerId] = target.dataset.jsContainerData.split(', ');

      const getCB = (html) => {
        document.querySelector(`#${containerId}`).innerHTML = html;
        if (isBtn) {
          target.classList.remove('loading');
        }
      };
      await jsKit.fetchUrl(url, html => getCB(html));
    },

    autofocus: (parentElem) => {
      const autofocusInput = parentElem.querySelector('[autofocus]');
      if (autofocusInput && !jsKit.isMobile()) {
        autofocusInput.focus();

        if (autofocusInput.tagName === 'INPUT' && autofocusInput.type === 'text') {
          const length = autofocusInput.value.length;
          autofocusInput.setSelectionRange(length, length);
        }
      }
    },

    toKebabCase: (str) => str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase(),

    initTooltip: (tooltipTriggerList) => {
      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      [...tooltipTriggerList].forEach((tooltipTriggerEl) => bootstrap.Tooltip.getOrCreateInstance(tooltipTriggerEl));
    },

    initPopover: (popoverTriggerList) => {
      const bootstrap = getBootstrap();
      if (!bootstrap) return;

      [...popoverTriggerList].forEach((popoverTriggerEl) => bootstrap.Popover.getOrCreateInstance(popoverTriggerEl));
    },

    runIfDataSetExists: (dataItem, fn) => {
      const selector = `[data-${jsKit.toKebabCase(dataItem)}]`;
      const el = document.querySelector(selector);

      if (el && typeof fn === 'function') {
        fn();
      }
    },

    attachEventByDataSet: (dataItem, fn, eventName = 'click') => {
      if (eventName === 'DOMContentLoaded') {
        jsKit.runIfDataSetExists(dataItem, fn);
        return;
      }

      delegatedEvents[eventName] = delegatedEvents[eventName] || {
        handlers: {},
        isBound: false,
      };

      delegatedEvents[eventName].handlers[dataItem] = delegatedEvents[eventName].handlers[dataItem] || [];
      delegatedEvents[eventName].handlers[dataItem].push({
        dataItem,
        fn,
        order: delegatedEventOrder,
      });
      delegatedEventOrder += 1;

      if (delegatedEvents[eventName].isBound) return;
      delegatedEvents[eventName].isBound = true;

      const useCapture = eventName === 'blur' || eventName === 'focus';

      document.addEventListener(eventName, (e) => {
        const eventConfig = delegatedEvents[eventName];
        const handlers = eventConfig ? eventConfig.handlers : {};
        const matches = [];
        const matchedDataItems = new Set();
        const target = e.target;
        let node = target && target.nodeType === Node.ELEMENT_NODE ? target : target && target.parentElement;

        while (node && node !== document) {
          if (node.dataset) {
            Object.keys(node.dataset).forEach((item) => {
              if (!handlers[item] || matchedDataItems.has(item)) return;

              matchedDataItems.add(item);
              handlers[item].forEach((handlerConfig) => {
                matches.push({
                  ...handlerConfig,
                  el: node,
                });
              });
            });
          }

          node = node.parentElement;
        }

        matches
          .sort((a, b) => a.order - b.order)
          .forEach(({ dataItem: item, fn: handler, el }) => {
            if (typeof handler !== 'function') return;

            e.delegateTarget = el;
            e.delegateDataItem = item;

          if (defaults.debugMode) {
            console.log('attachEventByDataSet match', item, eventName, el);
          }

            handler(e);
          });
      }, useCapture);
    },

    parseCurrentLocation: () => {
      const loc = window.location;
      return {
        protocol: loc.protocol,
        hostname: loc.hostname,
        port: loc.port,
        pathname: loc.pathname,
        search: loc.search,
        hash: loc.hash,
        host: loc.host,
      };
    },

    scrollToID: (id) => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },

    removeQueryParam: (paramName) => {
      const url = new URL(window.location.href);
      url.searchParams.delete(paramName);
      window.history.replaceState({}, '', url);
    },

    addQueryParam: (paramName, value) => {
      const url = new URL(window.location.href);
      url.searchParams.set(paramName, value);
      window.history.replaceState({}, '', url);
    },

    loadWithHistory: ({ url, state = {} }, cb) => {
      if (!url) {
        return;
      }

      // Anuluj poprzednie żądanie (jeśli było)
      if (jsKit.currentAbortController) {
        jsKit.currentAbortController.abort();
      }

      // Stwórz nowy kontroler dla tego żądania
      const abortController = new AbortController();
      jsKit.currentAbortController = abortController;
      const { signal } = abortController;

      history.pushState(state, '', url);

      fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
        },
        signal,
      })
        .then(res => {
          if (!res.ok) throw new Error('Fetch failed');
          return res.json();
        })
        .then((json) => {
          // Aborting a fetch that has already resolved does not prevent its queued
          // promise callbacks. Only the newest request may update the page.
          if (signal.aborted || jsKit.currentAbortController !== abortController) {
            return;
          }

          // Ustaw tytuł strony
          if (json.title) {
            document.title = json.title;
          }

          const [selector, html] = Object.entries(json)[0];
          const element = document.querySelector(selector);
          if (element) {
            element.innerHTML = html;
          }
          if (cb) {
            cb();
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') {
            // OK, poprzednie żądanie zostało anulowane
            console.log('[loadWithHistory] Request aborted:', url);
          } else {
            console.error('[loadWithHistory] Error loading:', err);
          }
        });
    },

    // automatyczne przeładowanie dla BACK tego co dodane jest poprzez history.pushState
    initPopState: () => {
      window.addEventListener('popstate', () => {
        jsKit.loadWithHistory({ url: window.location.href});
      });
    },

    sanitizeInteger: (target, minValue = 0, maxValue = null) => {
      const inputElement = target;
      if (!inputElement) {
        return null;
      }

      // Usunięcie znaków innych niż cyfry
      let value = inputElement.value.replace(/\D/g, '');
      // Pozwala na wyczyszczenie pola przez użytkownika
      if (value === '') {
        inputElement.value = '';
        return;
      }
      // Konwersja na liczbę i zaokrąglenie
      value = value ? Math.round(Number(value)) : 1;
      // Sprawdzenie wartości minimalnej
      if (minValue && value < minValue) {
        value = minValue;
      }
      // Sprawdzenie wartości maksymalnej
      if (maxValue && value > maxValue) {
        value = maxValue;
      }

      // Aktualizacja wartości w polu input
      inputElement.value = value;
    },

    showErrors: (data, context = 'body') => {
      if (defaults.debugMode) {
        console.log(`showErrors(${data})`);
      }

      if (typeof data !== 'undefined') {

        let generalError = typeof data.error_message !== 'undefined' && data.error_message;

        const errors = typeof data.error !== 'undefined' && data.error;

        const missingErrors = [];

        for (const key of Object.keys(errors || {})) {
          const contextElem = context instanceof Element ? context : document.querySelector(context);
          if (!contextElem) {
            continue;
          }
          const target = contextElem.querySelector(`[data-valid="${key}"]`);
          const errorClass = 'is-invalid';

          if (target) {
            if (errors[key] !== '') {
              if (defaults.debugMode) {
                console.log(`[data-valid="${key}"]`);
              }

              if (!target.classList.contains(errorClass)) {
                target.classList.add(errorClass);
                target.closest('.form-group')?.classList.add(errorClass);
                const feedback = target.nextElementSibling;
                if (feedback && feedback.classList.contains('invalid-feedback')) {
                  feedback.innerText = errors[key];
                }
                target.addEventListener('focus', () => {
                  target.classList.remove(errorClass);
                  target.closest('.form-group')?.classList.remove(errorClass);
                }, { once: true });
              }
            } else {
              target.classList.remove(errorClass);
              target.closest('.form-group')?.classList.remove(errorClass);
            }
          } else if (errors[key] !== '') {
            missingErrors.push(errors[key]);
          }
        }

        if (missingErrors.length) {
          const missingErrorsHtml = missingErrors.map((message) => `&bull; ${message}`).join('<br>');
          generalError = generalError ? `${generalError}<div class="missingError">${missingErrorsHtml}</div>` : missingErrorsHtml;
        }

        if (generalError) {
          jsKit.showNotificationsPanel(generalError);
        }
      }
    },

    hideErrors: (context = 'body') => {
      jsKit.hideNotificationsPanel();
      const contextElem = context instanceof Element ? context : document.querySelector(context);
      if (!contextElem) {
        return;
      }

      contextElem.querySelectorAll('[data-valid]').forEach((target) => {
        target.classList.remove('is-invalid');
        target.closest('.form-group')?.classList.remove('is-invalid');
        const feedback = target.nextElementSibling;
        if (feedback?.classList.contains('invalid-feedback')) {
          feedback.textContent = '';
        }
      });
    },

    showNotificationsPanel: (msg, type = 'error') => {
      const toastLive = document.getElementById('liveToast');
      const notificationsPanel = document.querySelector('[data-notifications-panel]');

      const bootstrap = getBootstrap();
      if (!bootstrap) return;
      if (toastLive && notificationsPanel) {
        const toast = new bootstrap.Toast(toastLive);
        const notificationsPanelMsg = notificationsPanel.querySelector('[data-notifications-panel-msg]');

        if (Array.isArray(msg) && msg.every((item) => typeof item === 'object' && item.content)) {
          msg.forEach((item) => {
            const paragraph = document.createElement('p');
            paragraph.textContent = item.content;
            notificationsPanelMsg.appendChild(paragraph);
          });
        } else if (typeof msg === 'string') {
          notificationsPanelMsg.innerHTML = msg;
        }

        toast.show();
        return;
      }

      const toastContainer = document.querySelector('.toast-container');
      if (!toastContainer || typeof msg !== 'string') return;

      const normalizedType = type === 'error' ? 'warning' : type === 'custom' ? 'info' : type;
      const toastElement = document.createElement('div');
      toastElement.className = 'toast';
      toastElement.setAttribute('role', 'alert');
      toastElement.setAttribute('aria-live', 'assertive');
      toastElement.setAttribute('aria-atomic', 'true');
      toastElement.innerHTML = `<div class="toast-header">
        <i class="icon-${normalizedType} text-${normalizedType} font-22"></i>
        <button type="button" class="btn-close ms-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">${msg}</div>`;
      toastContainer.appendChild(toastElement);
      bootstrap.Toast.getOrCreateInstance(toastElement).show();
    },

    hideNotificationsPanel: () => {
      document.querySelector('#message_panel #general_message')?.replaceChildren();
      document.querySelector('#message_panel .messagePanel')?.classList.remove('showPanel');
    },

    showNotification: (msg, type = 'info') => {
      if (typeof msg !== 'undefined') {
        jsKit.showNotificationsPanel(msg, type);
      }
    },

    showSuccess: (msg) => jsKit.showNotification(msg, 'success'),

    showInfo: (msg) => jsKit.showNotification(msg, 'info'),

    showWarning: (msg) => jsKit.showNotification(msg, 'warning'),

    jsonNotificationsPanel: (messages = []) => {
      messages.forEach(({ content, type = 'success' } = {}) => {
        if (content) {
          jsKit.showNotificationsPanel(content, type);
        }
      });
    },

    loadFlashMessages: async (section) => {
      if (!section) {
        return;
      }

      const data = await jsKit.requestUrl(`/main/loadFlashMessages/${section}`);
      jsKit.jsonNotificationsPanel(data?.messages || []);
    },

    installLegacyNotificationApi: () => {
      window.showNotificationsPanel = jsKit.showNotificationsPanel;
      window.hideNotificationsPanel = jsKit.hideNotificationsPanel;
      window.showErrors = jsKit.showErrors;
      window.hideErrors = jsKit.hideErrors;
      window.showNotification = jsKit.showNotification;
      window.showSuccess = jsKit.showSuccess;
      window.showInfo = jsKit.showInfo;
      window.showWarning = jsKit.showWarning;
      window.loadFlashMessages = jsKit.loadFlashMessages;
      window.jsonNotificationsPanel = jsKit.jsonNotificationsPanel;
    },

    validateInput: (input) => {
      const pattern = input.dataset.jsValidate;
      const invalidText = input.dataset.jsInvalidText;
      const invalidFeedbackElem = input.nextElementSibling;
      if (defaults.debugMode) {
        console.log(`inputValid(${input})`);
      }

      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(input.value)) {
        input.classList.remove('is-invalid');
      } else {
        input.classList.add('is-invalid');
        invalidFeedbackElem.innerText = invalidText;
      }

      return (regex.test(input.value));
    },

  };
})();

export default jsKit;
